/**
 * Session-level Ask AI cache + prefetch.
 *
 * The drawer unmounts on close, so without this every open pays two
 * sequential round-trips (list → resume messages) before anything renders.
 * The widget calls prefetchAskAi() when the bubble mounts; useAskAi hydrates
 * synchronously from the snapshot and revalidates in the background.
 */
import api from '@/shared/api/client'
import type { ChatSource, ConversationSummary, UiMessage } from '../types'

export const RESUME_WINDOW_MS = 30 * 60 * 1000

export interface AskAiSnapshot {
  conversations: ConversationSummary[]
  activeId: string | null
  messages: UiMessage[]
}

let snapshot: AskAiSnapshot | null = null
let inflight: Promise<AskAiSnapshot> | null = null

export function getAskAiSnapshot(): AskAiSnapshot | null {
  return snapshot
}

export function setAskAiSnapshot(next: AskAiSnapshot): void {
  snapshot = next
}

export function storedToUi(m: Record<string, unknown>): UiMessage {
  return {
    key: String(m.id ?? `stored-${Math.random().toString(36).slice(2)}`),
    role: m.role as 'user' | 'assistant',
    content: String(m.content ?? ''),
    sources: (m.sources as ChatSource[]) ?? [],
    pageType: (m.page_type as string) ?? null,
    pageSlug: (m.page_slug as string) ?? null,
    declined: m.status === 'declined',
    provenance: (m.provenance as UiMessage['provenance']) ?? undefined,
    trace: (m.tool_trace as UiMessage['trace']) ?? undefined,
    at: typeof m.created_at === 'string' ? m.created_at : undefined,
  }
}

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const res = await api.get('/ask-ai/conversations')
  return res.data?.data?.conversations ?? res.data?.conversations ?? []
}

export async function fetchMessages(conversationId: string): Promise<UiMessage[]> {
  const res = await api.get(`/ask-ai/conversations/${conversationId}`)
  const data = res.data?.data ?? res.data
  return ((data?.messages ?? []) as Array<Record<string, unknown>>).map(storedToUi)
}

/** Load list + resume thread into the snapshot. Deduped while in flight. */
export function prefetchAskAi(force = false): Promise<AskAiSnapshot> {
  if (!force && snapshot) return Promise.resolve(snapshot)
  if (inflight) return inflight
  inflight = (async () => {
    const conversations = await fetchConversations()
    let activeId: string | null = null
    let messages: UiMessage[] = []
    const newest = conversations[0]
    if (newest && Date.now() - new Date(newest.updated_at).getTime() < RESUME_WINDOW_MS) {
      messages = await fetchMessages(newest.id)
      activeId = newest.id
    }
    snapshot = { conversations, activeId, messages }
    return snapshot
  })().finally(() => {
    inflight = null
  })
  return inflight
}
